/**
 * Cortex Policies admin page (GDN-1816).
 *
 * v1: lists global policies, allows creating new drafts, and editing
 * existing drafts. Lifecycle transitions (verify citations / promote /
 * deprecate) deferred to a follow-up slice.
 */

import { useMemo, useState } from 'react';
import { Add, ContentCopy, Edit } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import {
  useCreateCortexPolicyMutation,
  useDeprecateCortexPolicyMutation,
  useGetCortexPolicyQuery,
  useListCortexPoliciesQuery,
  usePromoteCortexPolicyMutation,
  useSupersedeCortexPolicyMutation,
  useUpdateCortexPolicyMutation,
} from '../store/api';
import { formatDateTime } from '../utils/formatting';

type PolicyState = 'draft' | 'active' | 'deprecated';

const STATE_OPTIONS: Array<{ value: '' | PolicyState; label: string }> = [
  { value: '', label: 'All states' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'deprecated', label: 'Deprecated' },
];

function stateChipColor(state: string): 'default' | 'info' | 'success' | 'warning' {
  if (state === 'active') return 'success';
  if (state === 'draft') return 'info';
  if (state === 'deprecated') return 'warning';
  return 'default';
}

export default function CortexPoliciesPage() {
  const [stateFilter, setStateFilter] = useState<'' | PolicyState>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);

  const { data, isLoading, isFetching, error } = useListCortexPoliciesQuery({
    scope: 'global',
    state: stateFilter || undefined,
  });

  const items = useMemo(() => data?.items ?? [], [data]);

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>

        <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
          <Box>
            <h1 className="text-2xl font-bold text-gray-900">Cortex Policies</h1>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Global citation-grounded policies. Draft policies are editable;
              active and deprecated rows are read-only.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Select
              size="small"
              value={stateFilter}
              onChange={e => setStateFilter(e.target.value as '' | PolicyState)}
              sx={{ minWidth: 160 }}
            >
              {STATE_OPTIONS.map(opt => (
                <MenuItem key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateOpen(true)}
            >
              New Policy
            </Button>
          </Box>
        </div>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load policies. Try refreshing.
          </Alert>
        )}

        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Citations Verified</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {(isLoading || isFetching) && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                          Loading policies…
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No policies yet. Create one with the &ldquo;New Policy&rdquo; button.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {items.map(p => {
                  return (
                    <TableRow
                      key={p.policy_id}
                      hover
                      onClick={() => setEditingPolicyId(p.policy_id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {p.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={p.state}
                          color={stateChipColor(p.state)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {p.citations_verified ? (
                          <Chip label="Verified" color="success" size="small" variant="outlined" />
                        ) : (
                          <Chip label="Unverified" color="default" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(new Date(p.created_at))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(new Date(p.updated_at))}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation();
                              setEditingPolicyId(p.policy_id);
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </main>

      {createOpen && (
        <CreatePolicyDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {editingPolicyId && (
        <PolicyDialog
          policyId={editingPolicyId}
          onClose={() => setEditingPolicyId(null)}
          onOpenPolicy={id => setEditingPolicyId(id)}
        />
      )}
    </div>
  );
}

interface CreatePolicyDialogProps {
  open: boolean;
  onClose: () => void;
}

function CreatePolicyDialog({ open, onClose }: CreatePolicyDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [metadataText, setMetadataText] = useState('');
  const [metadataParseError, setMetadataParseError] = useState<string | null>(null);
  const [createPolicy, { isLoading }] = useCreateCortexPolicyMutation();

  const handleMetadataChange = (text: string) => {
    setMetadataText(text);
    if (text.trim() === '') {
      setMetadataParseError(null);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setMetadataParseError('Metadata must be a JSON object.');
      } else {
        setMetadataParseError(null);
      }
    } catch (e: any) {
      setMetadataParseError(e?.message || 'Invalid JSON.');
    }
  };

  const canSubmit =
    title.trim().length > 0 &&
    bodyMd.trim().length > 0 &&
    !metadataParseError &&
    !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const parsedMetadata =
      metadataText.trim() === '' ? undefined : JSON.parse(metadataText);
    try {
      await createPolicy({
        createPolicyRequest: {
          title: title.trim(),
          body_md: bodyMd,
          ...(parsedMetadata !== undefined ? { metadata: parsedMetadata } : {}),
        },
      }).unwrap();
      enqueueSnackbar('Policy created', { variant: 'success' });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.data?.message || 'Failed to create policy', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>New Policy</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            autoFocus
            fullWidth
            size="small"
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Body
            </Typography>
            <TextField
              value={bodyMd}
              onChange={e => setBodyMd(e.target.value)}
              fullWidth
              multiline
              minRows={10}
              placeholder="Markdown body..."
              slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.875rem' } } }}
            />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Metadata (JSON object — consumer extras like JotAudit selectors,
              applicability filters, legacy_rule_id back-link, etc.)
            </Typography>
            <TextField
              value={metadataText}
              onChange={e => handleMetadataChange(e.target.value)}
              fullWidth
              multiline
              minRows={5}
              error={!!metadataParseError}
              helperText={metadataParseError || 'Optional — leave empty for no metadata.'}
              placeholder='{ "applicability": { "cpt": ["99213"] } }'
              slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.8125rem' } } }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
          {isLoading ? <CircularProgress size={18} /> : 'Create draft'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface PolicyDialogProps {
  policyId: string;
  onClose: () => void;
  onOpenPolicy: (id: string) => void;
}

function PolicyDialog({ policyId, onClose, onOpenPolicy }: PolicyDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const copy = useCopyToClipboard();
  const { data: policy, isLoading } = useGetCortexPolicyQuery({ policyId });
  const [updatePolicy, { isLoading: isPatching }] = useUpdateCortexPolicyMutation();
  const [supersedePolicy, { isLoading: isSuperseding }] = useSupersedeCortexPolicyMutation();
  const [promotePolicy, { isLoading: isPromoting }] = usePromoteCortexPolicyMutation();
  const [deprecatePolicy, { isLoading: isDeprecating }] = useDeprecateCortexPolicyMutation();
  const isSaving = isPatching || isSuperseding;

  const isDraft = policy?.state === 'draft';
  const [title, setTitle] = useState<string | null>(null);
  const [bodyMd, setBodyMd] = useState<string | null>(null);
  // Metadata is round-tripped as raw JSON text so admins can hand-edit
  // arbitrary keys without a structured editor. Track parse errors so the
  // Save button can refuse a malformed payload.
  const [metadataText, setMetadataText] = useState<string | null>(null);
  const [metadataParseError, setMetadataParseError] = useState<string | null>(null);

  const effectiveTitle = title ?? policy?.title ?? '';
  const effectiveBody = bodyMd ?? policy?.body_md ?? '';
  const sourceMetadataText = policy
    ? JSON.stringify(policy.metadata || {}, null, 2)
    : '';
  const effectiveMetadataText = metadataText ?? sourceMetadataText;

  const metadataDirty = metadataText !== null && metadataText !== sourceMetadataText;
  const isDirty =
    (title !== null && title.trim() !== (policy?.title ?? '')) ||
    (bodyMd !== null && bodyMd !== (policy?.body_md ?? '')) ||
    metadataDirty;

  const handleMetadataChange = (text: string) => {
    setMetadataText(text);
    if (text.trim() === '') {
      setMetadataParseError(null);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setMetadataParseError('Metadata must be a JSON object.');
      } else {
        setMetadataParseError(null);
      }
    } catch (e: any) {
      setMetadataParseError(e?.message || 'Invalid JSON.');
    }
  };

  /**
   * Persist edits. Returns the policy_id to operate on (own id for in-place
   * patches; the new draft's id for supersede). Returns null if nothing was
   * saved (no edits / parse error / API failure — caller stops).
   */
  const persistEdits = async (): Promise<string | null> => {
    if (!policy) return null;
    if (metadataParseError) return null;
    if (!isDirty) return policy.policy_id;

    const parsedMetadata = metadataDirty
      ? metadataText && metadataText.trim() !== ''
        ? JSON.parse(metadataText)
        : {}
      : undefined;
    try {
      if (isDraft) {
        const patch: {
          title?: string;
          body_md?: string;
          metadata?: Record<string, unknown>;
        } = {};
        if (title !== null && title.trim() !== policy.title) patch.title = title.trim();
        if (bodyMd !== null && bodyMd !== policy.body_md) patch.body_md = bodyMd;
        if (parsedMetadata !== undefined) patch.metadata = parsedMetadata;
        await updatePolicy({
          policyId: policy.policy_id,
          updatePolicyRequest: patch,
        }).unwrap();
        return policy.policy_id;
      }
      const body: {
        title?: string;
        body_md?: string;
        metadata?: Record<string, unknown>;
      } = {};
      if (title !== null && title.trim() !== policy.title) body.title = title.trim();
      if (bodyMd !== null && bodyMd !== policy.body_md) body.body_md = bodyMd;
      if (parsedMetadata !== undefined) body.metadata = parsedMetadata;
      const newDraft = await supersedePolicy({
        policyId: policy.policy_id,
        supersedePolicyRequest: body,
      }).unwrap();
      return newDraft.policy_id;
    } catch (err: any) {
      enqueueSnackbar(err?.data?.message || 'Failed to save policy', { variant: 'error' });
      return null;
    }
  };

  const handleSave = async () => {
    const id = await persistEdits();
    if (!id || !policy) return;
    enqueueSnackbar(
      isDraft ? 'Policy updated' : 'New draft created — promote to activate',
      { variant: 'success' },
    );
    onClose();
  };

  const handleSaveAndPromote = async () => {
    const id = await persistEdits();
    if (!id) return;
    try {
      await promotePolicy({ policyId: id }).unwrap();
      enqueueSnackbar('Policy promoted to active', { variant: 'success' });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.data?.message || 'Failed to promote policy', { variant: 'error' });
    }
  };

  const handlePromoteOnly = async () => {
    if (!policy) return;
    try {
      await promotePolicy({ policyId: policy.policy_id }).unwrap();
      enqueueSnackbar('Policy promoted to active', { variant: 'success' });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.data?.message || 'Failed to promote policy', { variant: 'error' });
    }
  };

  const [deprecateConfirmOpen, setDeprecateConfirmOpen] = useState(false);

  const handleDeprecate = () => {
    if (!policy) return;
    setDeprecateConfirmOpen(true);
  };

  const confirmDeprecate = async () => {
    if (!policy) return;
    setDeprecateConfirmOpen(false);
    try {
      await deprecatePolicy({ policyId: policy.policy_id }).unwrap();
      enqueueSnackbar('Policy deprecated', { variant: 'success' });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.data?.message || 'Failed to deprecate policy', { variant: 'error' });
    }
  };

  return (
    <>
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isLoading || !policy
          ? 'Policy'
          : isDraft
            ? 'Edit Policy'
            : 'Edit Policy (will create a new draft)'}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        )}
        {!isLoading && policy && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={policy.state}
                color={stateChipColor(policy.state)}
                size="small"
                variant="outlined"
              />
              {policy.citations_verified && (
                <Chip label="Citations verified" color="success" size="small" variant="outlined" />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>
                  ID: <code>{policy.policy_id}</code>
                </span>
                <Tooltip title="Copy policy ID">
                  <IconButton
                    size="small"
                    onClick={() => copy(policy.policy_id, 'Policy ID')}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Typography>
            </Box>

            {policy.derived_via && (
              <Typography variant="caption" color="text.secondary">
                Provenance: <code>{policy.derived_via}</code>
              </Typography>
            )}

            {(policy.supersedes || (policy.superseded_by && policy.superseded_by.length > 0)) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  p: 1.5,
                  bgcolor: 'grey.50',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {policy.supersedes && (
                  <Typography variant="caption">
                    <strong>Supersedes:</strong>{' '}
                    <Button
                      size="small"
                      variant="text"
                      sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                      onClick={() => policy.supersedes && onOpenPolicy(policy.supersedes.policy_id)}
                    >
                      {policy.supersedes.title}
                    </Button>
                    <Chip
                      label={policy.supersedes.state}
                      color={stateChipColor(policy.supersedes.state)}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                    />
                  </Typography>
                )}
                {policy.superseded_by && policy.superseded_by.length > 0 && (
                  <Typography variant="caption">
                    <strong>Superseded by:</strong>{' '}
                    {policy.superseded_by.map((ref, i) => (
                      <span key={ref.policy_id}>
                        {i > 0 && ', '}
                        <Button
                          size="small"
                          variant="text"
                          sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                          onClick={() => onOpenPolicy(ref.policy_id)}
                        >
                          {ref.title}
                        </Button>
                        <Chip
                          label={ref.state}
                          color={stateChipColor(ref.state)}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 0.5, height: 18, fontSize: '0.7rem' }}
                        />
                      </span>
                    ))}
                  </Typography>
                )}
              </Box>
            )}

            <TextField
              label="Title"
              value={effectiveTitle}
              onChange={e => setTitle(e.target.value)}
              fullWidth
              size="small"
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block' }}
              >
                Body
              </Typography>
              <TextField
                value={effectiveBody}
                onChange={e => setBodyMd(e.target.value)}
                fullWidth
                multiline
                minRows={10}
                slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.875rem' } } }}
              />
            </Box>

            {/* Metadata editor — raw JSON object. Empty / blank means
                no metadata. Save is blocked when JSON is invalid. */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block' }}
              >
                Metadata (JSON object — consumer extras like JotAudit selectors,
                applicability filters, legacy_rule_id back-link, etc.)
              </Typography>
              <TextField
                value={effectiveMetadataText}
                onChange={e => handleMetadataChange(e.target.value)}
                fullWidth
                multiline
                minRows={5}
                error={!!metadataParseError}
                helperText={metadataParseError || 'Leave empty for no metadata.'}
                placeholder='{ "applicability": { "cpt": ["99213"] } }'
                slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.8125rem' } } }}
              />
            </Box>

            {!isDraft && (
              <Alert severity="info">
                {policy.state === 'active' ? 'Active' : 'Deprecated'} policies are
                immutable. Saving will create a new draft pointing at this row via{' '}
                <code>supersedes_policy_id</code>; the original stays live until the
                new draft passes citation verification and is promoted.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving || isPromoting || isDeprecating}>
          Cancel
        </Button>

        {/* Draft with edits — split: "Save as draft" (secondary) +
            "Save & Promote" (primary). */}
        {isDraft && isDirty && (
          <>
            <Button
              variant="outlined"
              disabled={!!metadataParseError || isSaving || isPromoting || isDeprecating}
              onClick={handleSave}
            >
              {isSaving && !isPromoting ? <CircularProgress size={18} /> : 'Save as draft'}
            </Button>
            <Tooltip
              title={
                policy?.supersedes
                  ? `Promoting will also deprecate: ${policy.supersedes.title}`
                  : 'Mark citations verified and promote to active'
              }
            >
              <Button
                variant="contained"
                color="success"
                disabled={!!metadataParseError || isSaving || isPromoting || isDeprecating}
                onClick={handleSaveAndPromote}
              >
                {isPromoting ? <CircularProgress size={18} /> : 'Save & Promote'}
              </Button>
            </Tooltip>
          </>
        )}

        {/* Draft with no edits — one primary action: promote. */}
        {isDraft && !isDirty && (
          <Tooltip
            title={
              policy?.supersedes
                ? `Promoting will also deprecate: ${policy.supersedes.title}`
                : 'Mark citations verified and promote to active'
            }
          >
            <Button
              variant="contained"
              color="success"
              disabled={isSaving || isPromoting || isDeprecating}
              onClick={handlePromoteOnly}
            >
              {isPromoting ? <CircularProgress size={18} /> : 'Promote to active'}
            </Button>
          </Tooltip>
        )}

        {/* Active source — offer Deprecate alongside the save (supersede) button. */}
        {policy && policy.state === 'active' && (
          <Button
            color="warning"
            variant="outlined"
            disabled={isSaving || isPromoting || isDeprecating}
            onClick={handleDeprecate}
          >
            {isDeprecating ? <CircularProgress size={18} /> : 'Deprecate'}
          </Button>
        )}

        {/* Active / deprecated source — saving creates a new draft via
            supersede. Promotion happens on the next pass. */}
        {policy && !isDraft && (
          <Button
            variant="contained"
            disabled={!isDirty || !!metadataParseError || isSaving || isPromoting || isDeprecating}
            onClick={handleSave}
          >
            {isSaving ? <CircularProgress size={18} /> : 'Save as new draft'}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/* Deprecate confirm — matches the dangerous-action dialog pattern used
        elsewhere in admin (e.g. org page). Avoids the native window.confirm
        which is blocked in some iframe / popup contexts. */}
    <Dialog
      open={deprecateConfirmOpen}
      onClose={() => setDeprecateConfirmOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Deprecate policy?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Deprecate <strong>&ldquo;{policy?.title}&rdquo;</strong>? Consumers will
          stop reading it. You can supersede with a new draft later if needed.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeprecateConfirmOpen(false)} disabled={isDeprecating}>
          Cancel
        </Button>
        <Button
          color="warning"
          variant="contained"
          onClick={confirmDeprecate}
          disabled={isDeprecating}
        >
          {isDeprecating ? <CircularProgress size={18} /> : 'Deprecate'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
